/**
 * SubstrateBatchDetailModal Component
 *
 * Detail view for a substrate batch showing overview, fill breakdown,
 * material usage, and status progression.
 */

import { useState, useEffect } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from '@/components/ui/table'
import { Loader2, Flame } from 'lucide-react'
import SubstrateStatusBadge, { getStatusConfig } from './SubstrateStatusBadge'
import { parseDateString } from '@/utils/dateUtils'
import { supabase } from '@/supabase-client'

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY

const STATUS_ORDER = ['MIXING', 'BAGGED', 'HEAT_CYCLING', 'COOLING', 'READY', 'DEPLETED']

const STATUS_LABELS = {
  MIXING: 'Mixing',
  BAGGED: 'Bagged',
  HEAT_CYCLING: 'Heat Cycle',
  COOLING: 'Cooling',
  READY: 'Ready',
  DEPLETED: 'Depleted'
}

async function fetchMaterialUsage(substrateBatchId) {
  const response = await fetch(`${SUPABASE_URL}/functions/v1/inventory-batches`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
      'apikey': SUPABASE_ANON_KEY
    },
    body: JSON.stringify({
      action: 'get-usage-by-substrate',
      substrate_batch_id: substrateBatchId
    }),
  })

  const result = await response.json()
  if (!response.ok) throw new Error(result.error || 'Failed to fetch material usage')
  return result.data
}

function formatDate(dateStr) {
  if (!dateStr) return '-'
  return parseDateString(dateStr).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  })
}

function formatCurrency(amount) {
  if (amount == null) return '-'
  return `£${Number(amount).toFixed(2)}`
}

function StatusStepper({ currentStatus }) {
  const currentIndex = STATUS_ORDER.indexOf(currentStatus)

  return (
    <div className="flex items-center justify-between w-full py-3">
      {STATUS_ORDER.map((status, index) => {
        const isCompleted = index < currentIndex
        const isCurrent = index === currentIndex
        const config = getStatusConfig(status)

        // Determine dot colour
        let dotClass = 'border-2 border-border-primary bg-surface-primary' // future: hollow
        if (isCompleted) {
          dotClass = 'bg-odin-green border-2 border-odin-green' // completed: green filled
        } else if (isCurrent) {
          // Current: use status colour
          const colorMap = {
            MIXING: 'bg-odin-blue border-2 border-odin-blue',
            BAGGED: 'bg-purple-500 border-2 border-purple-500',
            HEAT_CYCLING: 'bg-odin-red border-2 border-odin-red',
            COOLING: 'bg-amber-500 border-2 border-amber-500',
            READY: 'bg-odin-green border-2 border-odin-green',
            DEPLETED: 'bg-content-tertiary border-2 border-content-tertiary'
          }
          dotClass = colorMap[status] || dotClass
        }

        return (
          <div key={status} className="flex items-center flex-1 last:flex-none">
            <div className="flex flex-col items-center">
              <div className={`w-3 h-3 rounded-full ${dotClass}`} />
              <span className={`text-[10px] mt-1 whitespace-nowrap ${
                isCurrent ? 'font-medium text-content-primary' : 'text-content-tertiary'
              }`}>
                {STATUS_LABELS[status]}
              </span>
            </div>
            {index < STATUS_ORDER.length - 1 && (
              <div className={`flex-1 h-px mx-1 mt-[-12px] ${
                index < currentIndex ? 'bg-odin-green' : 'border-t border-dashed border-border-primary'
              }`} />
            )}
          </div>
        )
      })}
    </div>
  )
}

export default function SubstrateBatchDetailModal({ isOpen, onClose, batch }) {
  const [materials, setMaterials] = useState(null)
  const [materialsLoading, setMaterialsLoading] = useState(false)
  const [materialsError, setMaterialsError] = useState(null)
  const [heatCycle, setHeatCycle] = useState(null)

  useEffect(() => {
    if (!batch?.heat_cycle_id || !isOpen) {
      setHeatCycle(null)
      return
    }
    let cancelled = false
    supabase
      .from('heat_cycles')
      .select('id, cycle_code, start_time, end_time, energy_kwh, cost_gbp')
      .eq('id', batch.heat_cycle_id)
      .maybeSingle()
      .then(({ data, error: err }) => {
        if (cancelled) return
        if (err) {
          console.error('[SubstrateBatchDetailModal] heat_cycle fetch failed:', err)
        } else {
          setHeatCycle(data)
        }
      })
    return () => { cancelled = true }
  }, [batch?.heat_cycle_id, isOpen])

  useEffect(() => {
    if (!batch?.id || !isOpen) {
      setMaterials(null)
      return
    }

    let cancelled = false
    setMaterialsLoading(true)
    setMaterialsError(null)

    fetchMaterialUsage(batch.id)
      .then(data => {
        if (!cancelled) setMaterials(data)
      })
      .catch(err => {
        if (!cancelled) setMaterialsError(err.message)
      })
      .finally(() => {
        if (!cancelled) setMaterialsLoading(false)
      })

    return () => { cancelled = true }
  }, [batch?.id, isOpen])

  if (!batch) return null

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
        {/* Header */}
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="font-mono text-lg">{batch.batch_code}</DialogTitle>
            <SubstrateStatusBadge status={batch.status} />
          </div>
          {batch.recipe?.name && (
            <p className="text-sm text-content-secondary mt-1">{batch.recipe.name}</p>
          )}
        </DialogHeader>

        <div className="space-y-5">
          {/* Status Stepper */}
          <StatusStepper currentStatus={batch.status} />

          {/* Summary Grid */}
          <div className="grid grid-cols-2 gap-3">
            <Card className="p-3 bg-surface-secondary">
              <p className="text-xs text-content-tertiary">Total Blocks</p>
              <p className="text-lg font-semibold">
                {batch.total_blocks ?? <span className="text-sm font-normal text-content-tertiary italic">pending count</span>}
              </p>
            </Card>
            <Card className="p-3 bg-surface-secondary">
              <p className="text-xs text-content-tertiary">Available</p>
              <p className={`text-lg font-semibold ${batch.available_blocks > 0 ? 'text-odin-green' : 'text-content-tertiary'}`}>
                {batch.available_blocks ?? <span className="text-sm font-normal italic">—</span>}
              </p>
            </Card>
            <Card className="p-3 bg-surface-secondary">
              <p className="text-xs text-content-tertiary">Mixed Date</p>
              <p className="text-sm font-medium">{formatDate(batch.mixed_at)}</p>
            </Card>
            <Card className="p-3 bg-surface-secondary">
              <p className="text-xs text-content-tertiary">Cooled Date</p>
              <p className="text-sm font-medium">{formatDate(batch.cooled_at)}</p>
            </Card>
          </div>

          {/* Heat Cycle (auto-linked by materialiser) */}
          {heatCycle && (
            <Card className="p-3 bg-orange-50 border-orange-200">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2 text-xs text-content-tertiary">
                  <Flame className="h-3 w-3 text-orange-600" />
                  Heat Cycle
                </div>
                <span className="font-mono text-xs">{heatCycle.cycle_code}</span>
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-xs text-content-tertiary">Energy</p>
                  <p className="font-medium tabular-nums">
                    {heatCycle.energy_kwh != null ? `${heatCycle.energy_kwh.toFixed(1)} kWh` : '—'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-content-tertiary">Cost</p>
                  <p className="font-medium tabular-nums">
                    {heatCycle.cost_gbp != null ? formatCurrency(heatCycle.cost_gbp) : '—'}
                  </p>
                </div>
              </div>
            </Card>
          )}

          {/* Fill Breakdown */}
          {batch.fills?.length > 0 && (
            <div>
              <h4 className="text-sm font-medium mb-2">Fill Breakdown</h4>
              <Card>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Format</TableHead>
                      <TableHead className="text-right">Quantity</TableHead>
                      <TableHead className="text-right">Available</TableHead>
                      <TableHead className="text-right">Weight</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {batch.fills.map(fill => (
                      <TableRow key={fill.id}>
                        <TableCell className="text-sm">{fill.block_format?.name || '-'}</TableCell>
                        <TableCell className="text-right">{fill.quantity}</TableCell>
                        <TableCell className="text-right">
                          <span className={fill.available > 0 ? 'text-odin-green font-medium' : 'text-content-tertiary'}>
                            {fill.available}
                          </span>
                        </TableCell>
                        <TableCell className="text-right text-sm text-content-secondary">
                          {fill.block_format?.weight_kg ? `${fill.block_format.weight_kg} kg` : '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Card>
            </div>
          )}

          {/* Materials Used */}
          <div>
            <h4 className="text-sm font-medium mb-2">Materials Used</h4>
            {materialsLoading ? (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="h-5 w-5 animate-spin text-content-tertiary" />
              </div>
            ) : materialsError ? (
              <p className="text-sm text-odin-red py-2">{materialsError}</p>
            ) : materials?.materials?.length > 0 ? (
              <Card>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Material</TableHead>
                      <TableHead className="text-right">Qty</TableHead>
                      <TableHead>Unit</TableHead>
                      <TableHead className="text-right">Cost</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {materials.materials.map(mat => (
                      <TableRow key={mat.item_id}>
                        <TableCell className="text-sm">{mat.item_name}</TableCell>
                        <TableCell className="text-right text-sm">{mat.total_quantity}</TableCell>
                        <TableCell className="text-sm text-content-secondary">{mat.unit_of_measure}</TableCell>
                        <TableCell className="text-right text-sm">{formatCurrency(mat.total_cost)}</TableCell>
                      </TableRow>
                    ))}
                    <TableRow className="border-t-2">
                      <TableCell colSpan={3} className="text-sm font-medium">Total</TableCell>
                      <TableCell className="text-right text-sm font-medium">
                        {formatCurrency(materials.total_material_cost)}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Card>
            ) : (
              <Card className="p-4 bg-surface-secondary">
                <p className="text-sm text-content-tertiary text-center">No material records</p>
              </Card>
            )}
          </div>

          {/* Notes */}
          {batch.notes && (
            <div>
              <h4 className="text-sm font-medium mb-2">Notes</h4>
              <Card className="p-3 bg-surface-secondary">
                <p className="text-sm text-content-secondary whitespace-pre-wrap">{batch.notes}</p>
              </Card>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
